import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  AcademicCapIcon,
  UserGroupIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  ShieldCheckIcon,
  CogIcon,
  ClockIcon,
  BookOpenIcon,
  LockClosedIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  CheckCircleIcon,
  ArrowUpIcon,
} from '@heroicons/react/24/outline';

// ── Custom hook for counting animation ──
const useCountUp = (target, duration = 2000, startOnView = true) => {
  const [count, setCount] = useState(0);
  const ref = useRef();
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if (!startOnView) {
      let startTime;
      const step = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        setCount(Math.floor(progress * target));
        if (progress < 1) {
          requestAnimationFrame(step);
        }
      };
      requestAnimationFrame(step);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          let startTime;
          const step = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);
            setCount(Math.floor(progress * target));
            if (progress < 1) {
              requestAnimationFrame(step);
            }
          };
          requestAnimationFrame(step);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration, startOnView, hasAnimated]);

  return { count, ref };
};

// ── StatCard component (flat version) ──
const StatCard = ({ stat, delay }) => {
  const { count, ref } = useCountUp(stat.value, 2000, true);
  return (
    <div
      ref={ref}
      className="bg-white border border-slate-200 rounded-lg p-4 text-center transition hover:border-primary-200 animate-fade-up"
      style={{ animationDelay: `${delay}s` }}
    >
      <div className="w-10 h-10 mx-auto rounded-lg bg-primary-50 flex items-center justify-center text-primary-600 group-hover:bg-primary-100 transition">
        <stat.icon className="w-5 h-5" aria-hidden="true" />
      </div>
      <div className="mt-2 text-2xl font-bold text-slate-800">
        {count}{stat.suffix}
      </div>
      <div className="text-xs font-medium text-slate-400 uppercase tracking-wider">
        {stat.label}
      </div>
    </div>
  );
};

export default function LandingPage() {
  // ── scroll state for navbar & back‑to‑top ──
  const [scrolled, setScrolled] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 30);
      setShowBackToTop(y > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ── contact form state ──
  const [form, setForm] = useState({
    institution: '',
    name: '',
    phone: '',
    email: '',
    message: '',
  });
  const [formSent, setFormSent] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormSent(true);
    setTimeout(() => setFormSent(false), 4000);
    setForm({ institution: '', name: '', phone: '', email: '', message: '' });
  };

  // ── Intersection Observer for scroll animations ──
  const useOnScreen = (ref) => {
    const [isVisible, setIsVisible] = useState(false);
    useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => setIsVisible(entry.isIntersecting),
        { threshold: 0.1 }
      );
      if (ref.current) observer.observe(ref.current);
      return () => observer.disconnect();
    }, [ref]);
    return isVisible;
  };

  const featuresRef = useRef();
  const complianceRef = useRef();
  const pricingRef = useRef();
  const contactRef = useRef();
  const ctaRef = useRef();

  const featuresVisible = useOnScreen(featuresRef);
  const complianceVisible = useOnScreen(complianceRef);
  const pricingVisible = useOnScreen(pricingRef);
  const contactVisible = useOnScreen(contactRef);
  const ctaVisible = useOnScreen(ctaRef);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  // ── Analytics tracking function ──
  const trackEvent = (eventName, eventParams = {}) => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', eventName, eventParams);
    }
    if (typeof window.fbq !== 'undefined') {
      window.fbq('track', eventName, eventParams);
    }
    console.log(`[Analytics] ${eventName}`, eventParams);
  };

  // ── feature data ──
  const features = [
    {
      icon: UserGroupIcon,
      title: 'Student Management',
      desc: 'Track student records, attendance, promotion and performance easily.',
      color: 'text-primary-600',
    },
    {
      icon: ChartBarIcon,
      title: 'Teacher & Staff Tools',
      desc: 'Manage teachers, subjects, timetables and assignments efficiently.',
      color: 'text-secondary-600',
    },
    {
      icon: BookOpenIcon,
      title: 'Exams & Reports',
      desc: 'Automated grading system with printable report cards and analytics.',
      color: 'text-primary-600',
    },
    {
      icon: ChatBubbleLeftRightIcon,
      title: 'Central Communication',
      desc: 'Manage parent–teacher messaging in one place. Make traffic visible.',
      color: 'text-secondary-600',
    },
    {
      icon: ShieldCheckIcon,
      title: 'Enterprise Security',
      desc: 'Role-based access and auditability. Clear boundaries for every user.',
      color: 'text-primary-600',
    },
    {
      icon: CogIcon,
      title: 'Operational Efficiency',
      desc: 'Automate repetitive work. Move your team’s focus to quality.',
      color: 'text-secondary-600',
    },
  ];

  // ── compliance data ──
  const complianceItems = [
    {
      icon: LockClosedIcon,
      title: 'Role-Based Authorization',
      desc: 'Admin / teacher / parent / student roles defined separately. Permission matrix is visible and manageable — "least privilege" by default.',
    },
    {
      icon: ClockIcon,
      title: 'Audit Logs',
      desc: 'Who did what, when? Change history is traceable. Critical actions are logged for retrospective review.',
    },
    {
      icon: ShieldCheckIcon,
      title: 'School-Level Data Isolation',
      desc: 'Data does not mix between schools — isolated by default. Enables multi‑branch organisations to operate safely.',
    },
    {
      icon: BookOpenIcon,
      title: 'KVKK Approach',
      desc: 'Compliance-driven processes via data minimisation and access control. Collect only what is necessary.',
    },
    {
      icon: CogIcon,
      title: 'Backup & Retention Policy',
      desc: 'Continuity with recovery plans and clear retention rules. Configurable for enterprise requirements.',
    },
    {
      icon: UserGroupIcon,
      title: 'Permission Matrix',
      desc: 'Admin / teacher / parent / student boundaries are clear and manageable at every level.',
    },
  ];

  // ── pricing plans ──
  const plans = [
    {
      id: 'starter',
      name: 'Starter',
      price: 250,
      billing: 'per term',
      popular: false,
      features: [
        'Up to 500 Students',
        'Up to 20 Teachers',
        'Student Management',
        'Teacher Management',
        'Class Management',
        'Attendance Tracking',
        'Timetable',
        'Parent Portal',
        'Basic Reports',
      ],
    },
    {
      id: 'essential',
      name: 'Essential',
      price: 500,
      billing: 'per term',
      popular: true,
      features: [
        'Up to 750 Students',
        'Up to 30 Teachers',
        'Everything in Starter',
        'Finance Module',
        'Library Management',
        'Inventory Management',
        'Improved Reports',
        'Email & WhatsApp Support',
      ],
    },
    {
      id: 'professional',
      name: 'Professional',
      price: 1000,
      billing: 'per term',
      popular: false,
      features: [
        'Up to 1,500 Students',
        'Up to 100 Teachers',
        'Everything in Essential',
        'Advanced Results',
        'Advanced Reports & Analytics',
        'Bulk Operations',
        'Priority Support',
        'More Storage',
      ],
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 2000,
      billing: 'per term',
      popular: false,
      features: [
        'Unlimited Students',
        'Unlimited Teachers',
        'Everything in Professional',
        'Dedicated Onboarding & Training',
        'Custom Reports',
        'Dedicated Account Manager',
        'Highest Priority Support',
        'Maximum Storage Allocation',
      ],
    },
  ];

  const handlePlanSelect = (plan) => {
    trackEvent('plan_selected', {
      plan_name: plan.name,
      plan_id: plan.id,
      plan_price: plan.price,
      plan_popular: plan.popular,
    });
  };

  // ── Hero stats data ──
  const statsData = [
    { icon: UserGroupIcon, label: 'Students', value: 2847, suffix: '' },
    { icon: UserIcon, label: 'Teachers', value: 186, suffix: '' },
    { icon: ChartBarIcon, label: 'Pass Rate', value: 94, suffix: '%' },
    { icon: ClockIcon, label: 'Uptime', value: 99.9, suffix: '%' },
  ];

  // Keep one preferred domain everywhere to prevent duplicate URLs in Google.
  const canonicalUrl = 'https://pawaschool.net/';
  const socialImageUrl = 'https://pawaschool.net/assets/pawaschool-logo.png';

  return (
    <>
      {/* ─── HELMET (Head metadata) ─── */}
      <Helmet>
        <html lang="en" />
        <title>PawaSchool – School Management System for Sierra Leone</title>
        <meta
          name="description"
          content="PawaSchool is a school management platform for Sierra Leone. Manage students, teachers, classes, attendance, examinations, results and reports in one system."
        />
        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />
        <meta
          name="googlebot"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:title" content="PawaSchool – School Management System" />
        <meta
          property="og:description"
          content="Manage students, teachers, classes, examinations and school reports with PawaSchool."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="PawaSchool" />
        <meta property="og:locale" content="en_SL" />
        <meta property="og:image" content={socialImageUrl} />
        <meta property="og:image:alt" content="PawaSchool school management platform" />

        {/* Twitter Cards */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="PawaSchool – School Management System" />
        <meta
          name="twitter:description"
          content="Manage students, teachers, classes, examinations and school reports with PawaSchool."
        />
        <meta name="twitter:image" content={socialImageUrl} />
        <meta name="twitter:image:alt" content="PawaSchool school management platform" />

        {/* Organisation structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            '@id': `${canonicalUrl}#organization`,
            name: 'PawaSchool',
            url: canonicalUrl,
            description:
              'PawaSchool is a school management platform for schools in Sierra Leone.',
            logo: {
              '@type': 'ImageObject',
              url: socialImageUrl,
            },
            email: 'info@pawaschool.net',
          })}
        </script>

        {/* Website structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            '@id': `${canonicalUrl}#website`,
            name: 'PawaSchool',
            url: canonicalUrl,
            description:
              'A modern school management system for schools in Sierra Leone.',
            publisher: {
              '@id': `${canonicalUrl}#organization`,
            },
            inLanguage: 'en',
          })}
        </script>

        {/* Software structured data: avoids incorrect Product snippet warnings */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            '@id': `${canonicalUrl}#software`,
            name: 'PawaSchool',
            url: canonicalUrl,
            description:
              'An all-in-one school management platform for student records, teachers, classes, attendance, examinations, results, reports and school administration.',
            applicationCategory: 'EducationalApplication',
            applicationSubCategory: 'School Management System',
            operatingSystem: 'Web',
            browserRequirements:
              'Requires a modern web browser and internet connection',
            softwareVersion: '1.0',
            publisher: {
              '@id': `${canonicalUrl}#organization`,
            },
            offers: {
              '@type': 'AggregateOffer',
              url: `${canonicalUrl}apply`,
              priceCurrency: 'SLE',
              lowPrice: '250',
              highPrice: '2000',
              offerCount: '4',
              availability: 'https://schema.org/InStock',
            },
          })}
        </script>

        {/* Home page structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            '@id': `${canonicalUrl}#webpage`,
            url: canonicalUrl,
            name: 'PawaSchool - School Management System for Sierra Leone',
            description:
              'PawaSchool helps schools manage students, teachers, attendance, classes, examinations, results, fees and academic records.',
            isPartOf: {
              '@id': `${canonicalUrl}#website`,
            },
            about: {
              '@id': `${canonicalUrl}#software`,
            },
            inLanguage: 'en',
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-slate-50">
        {/* ─── NAVBAR (flat) ─── */}
        <header
          role="banner"
          className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
            scrolled
              ? 'bg-white border-b border-slate-200 py-2 sm:py-3'
              : 'bg-white/80 py-3 sm:py-5'
          }`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 flex justify-between items-center">
            <Link to="/" className="flex items-center space-x-2" aria-label="PawaSchool Home">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-primary-600 flex items-center justify-center">
                <AcademicCapIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" aria-hidden="true" />
              </div>
              <span className="text-lg sm:text-xl font-extrabold text-primary-600 tracking-tight">
                PawaSchool
              </span>
            </Link>

            <nav className="hidden md:flex items-center space-x-6 lg:space-x-8 text-sm font-medium text-slate-600">
              <button onClick={() => scrollTo('features')} className="hover:text-primary-600 transition">
                Features
              </button>
              <button onClick={() => scrollTo('compliance')} className="hover:text-primary-600 transition">
                Compliance
              </button>
              <button onClick={() => scrollTo('pricing')} className="hover:text-primary-600 transition">
                Pricing
              </button>
              <button onClick={() => scrollTo('contact')} className="hover:text-primary-600 transition">
                Contact
              </button>
            </nav>

            <div className="flex items-center space-x-3 sm:space-x-4">
              <Link
                to="/apply"
                className="hidden sm:inline-block text-primary-600 hover:text-primary-700 font-medium transition text-sm"
              >
                Get Started
              </Link>
              <Link
                to="/login"
                className="px-4 py-2 sm:px-5 sm:py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-sm"
              >
                Login
              </Link>
            </div>
          </div>
        </header>

        {/* ─── HERO ─── */}
        <section
          className="min-h-screen flex items-center pt-24 sm:pt-28 pb-12 sm:pb-16 px-4 sm:px-6 md:px-12 relative overflow-hidden"
          aria-labelledby="hero-heading"
        >
          {/* decorative backgrounds */}
          <div className="absolute -top-20 -right-20 w-64 sm:w-80 h-64 sm:h-80 bg-primary-200/20 rounded-full blur-3xl animate-float"></div>
          <div className="absolute -bottom-20 -left-20 w-64 sm:w-80 h-64 sm:h-80 bg-secondary-200/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>

          <div className="max-w-7xl mx-auto w-full grid md:grid-cols-2 gap-8 lg:gap-12 items-center relative z-10">
            <div className="space-y-4 sm:space-y-6 animate-fade-up">
              <span className="inline-flex items-center gap-2 bg-primary-100 text-primary-700 text-xs font-semibold px-3 py-1.5 rounded-full">
                <span className="w-2 h-2 bg-primary-500 rounded-full animate-pulse"></span>
                Next‑Gen School Management
              </span>
              <h1 id="hero-heading" className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-slate-800 leading-tight">
                Modern School Management <br />
                <span className="text-primary-600">
                  for Sierra Leone
                </span>
              </h1>
              <p className="text-base sm:text-lg text-slate-500 max-w-lg">
                Manage students, teachers, classes, exams and reports in one powerful platform designed for schools.
              </p>
              <div className="flex flex-wrap gap-3 sm:gap-4 pt-2">
                <Link
                  to="/apply"
                  className="px-6 sm:px-8 py-3 sm:py-3.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition font-semibold flex items-center gap-2 text-sm sm:text-base"
                >
                  Get Started
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
                <button
                  onClick={() => scrollTo('pricing')}
                  className="px-6 sm:px-8 py-3 sm:py-3.5 border-2 border-slate-200 text-slate-700 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition font-medium text-sm sm:text-base"
                >
                  View Pricing
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-4 sm:gap-6 pt-2 text-xs sm:text-sm text-slate-400">
                <span className="flex items-center gap-1.5">
                  <CheckCircleIcon className="w-4 h-4 text-primary-500" aria-hidden="true" /> Go Paperless
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircleIcon className="w-4 h-4 text-primary-500" aria-hidden="true" /> Go Digital
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircleIcon className="w-4 h-4 text-primary-500" aria-hidden="true" /> Simple setup
                </span>
              </div>
            </div>

            {/* ─── STATS BLOCK (flat) ─── */}
            <div className="relative flex justify-center mt-8 md:mt-0">
              <div className="relative w-full max-w-xs sm:max-w-sm md:max-w-md">
                <div className="absolute inset-0 bg-primary-100/30 rounded-3xl blur-2xl"></div>
                <div className="relative bg-white rounded-3xl p-6 sm:p-8 border border-slate-200">
                  <div className="grid grid-cols-2 gap-3 sm:gap-4">
                    {statsData.map((stat, i) => (
                      <StatCard key={i} stat={stat} delay={0.2 + i * 0.1} />
                    ))}
                  </div>

                  {/* Schools joined badge */}
                  <div className="mt-4 sm:mt-5 flex justify-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 sm:px-5 sm:py-2.5 rounded-full bg-primary-50 border border-primary-200">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-500"></span>
                      </span>
                      <span className="text-xs sm:text-sm font-semibold text-slate-700">
                        <span className="text-primary-600">+12</span> schools joined this month
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─── FEATURES (flat cards) ─── */}
        <section id="features" ref={featuresRef} className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 md:px-12 bg-white" aria-labelledby="features-heading">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10 sm:mb-12">
              <span className="inline-block text-xs font-semibold tracking-wider uppercase text-primary-600 bg-primary-50 px-4 py-1.5 rounded-full">
                Features
              </span>
              <h2 id="features-heading" className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-800 mt-4">
                Everything You Need to Run a Modern School
              </h2>
              <p className="mt-3 text-slate-500 max-w-2xl mx-auto text-base sm:text-lg">
                From academics to operations — all in one unified platform.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto">
              {features.map((feature, i) => (
                <div
                  key={i}
                  className={`p-5 sm:p-6 bg-white border border-slate-200 rounded-lg hover:border-primary-300 transition ${
                    featuresVisible ? 'animate-fade-up' : 'opacity-0'
                  }`}
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-primary-50 flex items-center justify-center ${feature.color}`}>
                    <feature.icon className="w-5 h-5 sm:w-6 sm:h-6" aria-hidden="true" />
                  </div>
                  <h3 className="mt-4 sm:mt-5 font-semibold text-base sm:text-lg text-slate-800">
                    {feature.title}
                  </h3>
                  <p className="text-slate-500 mt-2 text-sm leading-relaxed">
                    {feature.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── COMPLIANCE (flat cards) ─── */}
        <section id="compliance" ref={complianceRef} className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 md:px-12 bg-slate-50" aria-labelledby="compliance-heading">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10 sm:mb-12">
              <span className="inline-block text-xs font-semibold tracking-wider uppercase text-primary-600 bg-primary-50 px-4 py-1.5 rounded-full">
                Compliance & Security
              </span>
              <h2 id="compliance-heading" className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-800 mt-4">
                Enterprise‑Grade Governance
              </h2>
              <p className="mt-3 text-slate-500 max-w-2xl mx-auto text-base sm:text-lg">
                Built‑in security, audit trails, and data isolation for safe operations.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 max-w-5xl mx-auto">
              {complianceItems.map((item, i) => (
                <div
                  key={i}
                  className={`bg-white border border-slate-200 rounded-lg p-5 sm:p-6 hover:border-primary-200 transition ${
                    complianceVisible ? 'animate-fade-up' : 'opacity-0'
                  }`}
                  style={{ animationDelay: `${i * 0.07}s` }}
                >
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-primary-50 flex items-center justify-center text-primary-600">
                    <item.icon className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" />
                  </div>
                  <h3 className="font-semibold text-slate-800 mt-3 sm:mt-4 text-sm sm:text-base">{item.title}</h3>
                  <p className="text-slate-500 mt-1 text-xs sm:text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-12 sm:mt-14 text-center bg-primary-50 border border-primary-200 rounded-3xl p-6 sm:p-10">
              <h3 className="text-xl sm:text-2xl font-bold text-slate-800">Let’s plan an enterprise demo.</h3>
              <p className="text-slate-500 mt-2 max-w-lg mx-auto text-sm sm:text-base">
                We’ll map the right modules for your school and walk through a live scenario with your team.
              </p>
              <button
                onClick={() => scrollTo('contact')}
                className="mt-5 sm:mt-6 inline-block px-6 sm:px-8 py-3 sm:py-3.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition font-semibold text-sm sm:text-base"
              >
                Contact Us Now
              </button>
            </div>
          </div>
        </section>

        {/* ─── PRICING (flat cards) ─── */}
        <section id="pricing" ref={pricingRef} className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 md:px-12 bg-white" aria-labelledby="pricing-heading">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10 sm:mb-12">
              <span className="inline-block text-xs font-semibold tracking-wider uppercase text-primary-600 bg-primary-50 px-4 py-1.5 rounded-full">
                Pricing
              </span>
              <h2 id="pricing-heading" className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-800 mt-4">
                Choose the Perfect Plan for Your School
              </h2>
              <p className="mt-3 text-slate-500 max-w-2xl mx-auto text-base sm:text-lg">
                Whether you're a small school or a large institution, PawaSchool has a plan that grows with you. All prices shown are charged per academic term.
              </p>
              <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-4 py-2 text-xs sm:text-sm font-semibold text-primary-700">
                <ClockIcon className="h-4 w-4" aria-hidden="true" />
                One payment covers one academic term
              </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {plans.map((plan, i) => (
                <div
                  key={i}
                  className={`bg-white border rounded-lg p-5 sm:p-7 transition hover:border-primary-300 ${
                    plan.popular
                      ? 'border-primary-500 ring-2 ring-primary-200 relative'
                      : 'border-slate-200'
                  } ${pricingVisible ? 'animate-fade-up' : 'opacity-0'}`}
                  style={{ animationDelay: `${i * 0.08}s` }}
                >
                  {plan.popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary-600 text-white text-[10px] sm:text-xs font-bold px-3 sm:px-4 py-1 rounded-full shadow-lg whitespace-nowrap">
                      Most Popular
                    </span>
                  )}
                  <h3 className="text-base sm:text-lg font-bold text-slate-800">{plan.name}</h3>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="text-2xl sm:text-3xl font-extrabold text-primary-600">
                      {plan.price.toLocaleString()}
                    </span>
                    <span className="text-xs sm:text-sm text-slate-400">SLE</span>
                  </div>
                  <div className="mt-2 inline-flex items-center rounded-md bg-primary-50 px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-primary-700">
                    {plan.billing}
                  </div>
                  <p className="mt-2 text-xs leading-relaxed text-slate-400">
                    Pay {plan.price.toLocaleString()} SLE for each academic term.
                  </p>
                  <ul className="mt-4 sm:mt-5 space-y-2 text-xs sm:text-sm text-slate-500" role="list">
                    {plan.features.map((f, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <CheckCircleIcon className="w-3 h-3 sm:w-4 sm:h-4 text-primary-500 flex-shrink-0" aria-hidden="true" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/apply"
                    className={`mt-5 sm:mt-6 block text-center py-2 sm:py-2.5 rounded-lg font-semibold transition text-sm sm:text-base ${
                      plan.popular
                        ? 'bg-primary-600 text-white hover:bg-primary-700'
                        : 'border-2 border-slate-200 text-slate-700 hover:border-primary-300 hover:bg-primary-50'
                    }`}
                    onClick={() => handlePlanSelect(plan)}
                  >
                    Choose {plan.name}
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── FINAL CALL TO ACTION ─── */}
        <section id="cta" ref={ctaRef} className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 md:px-12 bg-primary-600 text-white" aria-labelledby="cta-heading">
          <div className="max-w-4xl mx-auto text-center">
            <h2 id="cta-heading" className={`text-2xl sm:text-3xl md:text-5xl font-extrabold leading-tight ${ctaVisible ? 'animate-fade-up' : 'opacity-0'}`}>
              Ready to Transform Your School?
            </h2>
            <p className={`mt-3 sm:mt-4 text-base sm:text-lg text-primary-100 max-w-2xl mx-auto ${ctaVisible ? 'animate-fade-up' : 'opacity-0'}`} style={{ animationDelay: '0.1s' }}>
              Join forward-thinking educational institutions using technology to simplify administration, improve collaboration, and deliver better educational outcomes.
            </p>
            <p className={`mt-1 sm:mt-2 text-primary-200 text-xs sm:text-sm ${ctaVisible ? 'animate-fade-up' : 'opacity-0'}`} style={{ animationDelay: '0.15s' }}>
              Start your digital transformation today.
            </p>
            <div className={`flex flex-wrap justify-center gap-3 sm:gap-4 mt-6 sm:mt-8 ${ctaVisible ? 'animate-fade-up' : 'opacity-0'}`} style={{ animationDelay: '0.2s' }}>
              <Link
                to="/apply"
                className="px-6 sm:px-8 py-3 sm:py-3.5 bg-white text-primary-700 rounded-lg hover:bg-slate-100 transition font-semibold text-sm sm:text-base"
              >
                Get Started
              </Link>
              <button
                onClick={() => scrollTo('contact')}
                className="px-6 sm:px-8 py-3 sm:py-3.5 border-2 border-white/50 text-white rounded-lg hover:bg-white/10 transition font-semibold text-sm sm:text-base"
              >
                Free Consultation
              </button>
            </div>
          </div>
        </section>

        {/* ─── CONTACT (flat form card) ─── */}
        <section id="contact" ref={contactRef} className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 md:px-12 bg-slate-50" aria-labelledby="contact-heading">
          <div className="max-w-7xl mx-auto grid lg:grid-cols-5 gap-8 lg:gap-12">
            <div className={`lg:col-span-2 space-y-5 sm:space-y-6 ${contactVisible ? 'animate-fade-up' : 'opacity-0'}`}>
              <span className="inline-block text-xs font-semibold tracking-wider uppercase text-primary-600 bg-primary-100 px-4 py-1.5 rounded-full">
                Contact
              </span>
              <h2 id="contact-heading" className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-800 leading-tight">
                Let’s Shape the Right Solution <br />
                <span className="text-primary-600">
                  for Your School
                </span>
              </h2>
              <p className="text-slate-500 text-sm sm:text-base">
                Contact us for tailored PawaSchool planning, proposal structuring, and rollout guidance.
              </p>

              <div className="space-y-3 sm:space-y-4 pt-2 sm:pt-4">
                <div className="flex items-center gap-3 sm:gap-4 text-sm">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-primary-50 flex items-center justify-center text-primary-600">
                    <PhoneIcon className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="text-slate-400 text-xs">Call us</div>
                    <a href="tel:+23277878844" className="font-medium text-slate-700 hover:text-primary-600 transition text-sm sm:text-base">
                      +23277878844
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-3 sm:gap-4 text-sm">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-green-50 flex items-center justify-center text-green-600">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </div>
                  <div>
                    <div className="text-slate-400 text-xs">WhatsApp</div>
                    <a href="#" className="font-medium text-slate-700 hover:text-green-600 transition text-sm sm:text-base">
                      Message us on WhatsApp
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-3 sm:gap-4 text-sm">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600">
                    <EnvelopeIcon className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="text-slate-400 text-xs">Email</div>
                    <a href="mailto:info@pawaschool.net" className="font-medium text-slate-700 hover:text-blue-600 transition text-sm sm:text-base">
                      info@pawaschool.net
                    </a>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 sm:gap-4 pt-1 sm:pt-2">
                <a href="#" aria-label="Twitter" className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-primary-100 hover:text-primary-600 transition">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                </a>
                <a href="#" aria-label="Facebook" className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-primary-100 hover:text-primary-600 transition">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 01-1.93.07 4.28 4.28 0 004 2.98 8.521 8.521 0 01-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z"/>
                  </svg>
                </a>
                <a href="#" aria-label="Instagram" className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-primary-100 hover:text-primary-600 transition">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
              </div>
            </div>

            <div className={`lg:col-span-3 bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 ${contactVisible ? 'animate-fade-up' : 'opacity-0'}`} style={{ animationDelay: '0.2s' }}>
              <h3 className="text-lg sm:text-xl font-bold text-slate-800">We're Listening to Your School</h3>
              <p className="text-sm text-slate-400 mt-1">Fill out the form and our team will get back to you soon.</p>

              <form onSubmit={handleSubmit} className="mt-5 sm:mt-6 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <input
                    name="institution"
                    placeholder="Institution Name"
                    value={form.institution}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition"
                    required
                    aria-label="Institution name"
                  />
                  <input
                    name="name"
                    placeholder="Authorized Full Name"
                    value={form.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition"
                    required
                    aria-label="Full name"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <input
                    name="phone"
                    placeholder="Phone Number"
                    value={form.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition"
                    required
                    aria-label="Phone number"
                  />
                  <input
                    name="email"
                    placeholder="E-Mail Address"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition"
                    required
                    aria-label="Email address"
                  />
                </div>
                <textarea
                  name="message"
                  placeholder="Your Message"
                  rows="4"
                  value={form.message}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition"
                  required
                  aria-label="Message"
                ></textarea>
                <button
                  type="submit"
                  className="w-full py-3.5 bg-primary-600 text-white rounded-lg font-semibold text-sm hover:bg-primary-700 transition flex items-center justify-center gap-2"
                >
                  {formSent ? (
                    <>
                      <CheckCircleIcon className="w-5 h-5" aria-hidden="true" /> Message Sent!
                    </>
                  ) : (
                    <>
                      Send Message
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </section>

        {/* ─── FOOTER ─── */}
        <footer role="contentinfo" className="bg-slate-900 text-slate-300 pt-12 sm:pt-16 pb-6 sm:pb-8 px-4 sm:px-6 md:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 pb-10 sm:pb-12 border-b border-slate-700/50">
              <div>
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-primary-500 flex items-center justify-center">
                    <AcademicCapIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" aria-hidden="true" />
                  </div>
                  <span className="text-lg sm:text-xl font-extrabold text-white">PawaSchool</span>
                </div>
                <p className="text-sm text-slate-400 mt-3 max-w-xs leading-relaxed">
                  Digitize Education with Paperless Modern school management for the next generation.
                </p>
                <p className="text-xs text-slate-500 mt-4">
                  © {new Date().getFullYear()} PawaSchool. All rights reserved.
                </p>
              </div>

              <div>
                <h4 className="text-white font-semibold text-sm mb-4">SERVICES & SOLUTIONS</h4>
                <ul className="space-y-2.5 text-sm">
                  <li><a href="#" className="hover:text-white transition">Academic Tracking</a></li>
                  <li><a href="#" className="hover:text-white transition">Question / Exam Editor</a></li>
                  <li><a href="#" className="hover:text-white transition">Compliance & Security</a></li>
                </ul>
              </div>

              <div>
                <h4 className="text-white font-semibold text-sm mb-4">EXPLORE</h4>
                <ul className="space-y-2.5 text-sm">
                  <li><a href="#" className="hover:text-white transition">About</a></li>
                  <li><a href="#" className="hover:text-white transition">Blog</a></li>
                  <li><a href="#" className="hover:text-white transition">PawaSchool Login</a></li>
                  <li><a href="#" className="hover:text-white transition">Contact</a></li>
                </ul>
              </div>

              <div>
                <h4 className="text-white font-semibold text-sm mb-4">CONTACT</h4>
                <ul className="space-y-2.5 text-sm text-slate-400">
                  <li className="flex items-start gap-2">
                    <svg className="w-4 h-4 text-primary-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span>Bo, Southern, SIERRA LEONE</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <PhoneIcon className="w-4 h-4 text-primary-400" aria-hidden="true" />
                    <a href="tel:+23277878844" className="hover:text-white transition">+23277878844</a>
                  </li>
                  <li className="flex items-center gap-2">
                    <EnvelopeIcon className="w-4 h-4 text-primary-400" aria-hidden="true" />
                    <a href="mailto:info@pawaschool.net" className="hover:text-white transition">info@pawaschool.net</a>
                  </li>
                </ul>
                <div className="flex gap-3 mt-4">
                  <a href="#" aria-label="Twitter" className="text-slate-400 hover:text-white transition">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                    </svg>
                  </a>
                  <a href="#" aria-label="Facebook" className="text-slate-400 hover:text-white transition">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 01-1.93.07 4.28 4.28 0 004 2.98 8.521 8.521 0 01-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z"/>
                    </svg>
                  </a>
                  <a href="#" aria-label="Instagram" className="text-slate-400 hover:text-white transition">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            <div className="pt-6 flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4 text-xs text-slate-500">
              <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
                <a href="#" className="hover:text-white transition">Privacy Policy</a>
                <a href="#" className="hover:text-white transition">Terms of Use</a>
                <a href="#" className="hover:text-white transition">Cookie Preferences</a>
              </div>
              <span className="text-center">PawaSchool is a brand of Pawa Education Services Ltd.</span>
            </div>
          </div>
        </footer>

        {/* ─── BACK TO TOP BUTTON ─── */}
        {showBackToTop && (
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="fixed bottom-6 right-6 z-50 p-3 bg-primary-600 text-white rounded-lg shadow-lg hover:bg-primary-700 transition"
            aria-label="Back to top"
          >
            <ArrowUpIcon className="w-5 h-5" />
          </button>
        )}

        {/* ─── Custom CSS animations ─── */}
        <style>{`
          @keyframes fadeUp {
            0% { opacity: 0; transform: translateY(30px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }
          .animate-fade-up {
            animation: fadeUp 0.8s ease forwards;
          }
          .animate-float {
            animation: float 4s ease-in-out infinite;
          }
          .opacity-0 {
            opacity: 0;
          }
        `}</style>
      </div>
    </>
  );
}